// Streak Tracking Management System

/**
 * Get today's date string (YYYY-MM-DD)
 */
export const getTodayString = () => {
  const today = new Date();
  return today.toISOString().split('T')[0];
};

/**
 * Get yesterday's date string (YYYY-MM-DD)
 */
export const getYesterdayString = () => {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return yesterday.toISOString().split('T')[0];
};

/**
 * Calculate days between two dates
 */
export const daysBetween = (date1, date2) => {
  const d1 = new Date(date1);
  const d2 = new Date(date2);
  const diffTime = Math.abs(d2 - d1);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * Check if user has played today
 */
export const hasPlayedToday = (lastPlayedDate) => {
  if (!lastPlayedDate) return false;
  return lastPlayedDate === getTodayString();
};

/**
 * Update streak based on activity
 * @param {Object} streakData - Current streak data
 * @returns {Object} Updated streak data
 */
export const updateStreak = (streakData = {}) => {
  const today = getTodayString();
  const yesterday = getYesterdayString();
  
  const currentStreak = streakData.currentStreak || 0;
  const lastPlayedDate = streakData.lastPlayedDate;
  const longestStreak = streakData.longestStreak || 0;
  const totalDaysPlayed = streakData.totalDaysPlayed || 0;
  const streakFreezeUsed = streakData.streakFreezeUsed || false;
  const activityDates = streakData.activityDates || [];

  // Already played today
  if (lastPlayedDate === today) {
    return streakData;
  }

  let newStreak = currentStreak;
  let newStreakFreezeUsed = streakFreezeUsed;

  // First time playing
  if (!lastPlayedDate) {
    newStreak = 1;
  }
  // Played yesterday - continue streak
  else if (lastPlayedDate === yesterday) {
    newStreak = currentStreak + 1;
    newStreakFreezeUsed = false; // Reset freeze after successful day
  }
  // Missed yesterday but have freeze available
  else if (daysBetween(lastPlayedDate, today) === 2 && !streakFreezeUsed) {
    newStreak = currentStreak + 1;
    newStreakFreezeUsed = true; // Use freeze
  }
  // Streak broken
  else {
    newStreak = 1;
    newStreakFreezeUsed = false;
  }

  // Update activity dates
  const newActivityDates = [...new Set([...activityDates, today])];

  return {
    currentStreak: newStreak,
    longestStreak: Math.max(newStreak, longestStreak),
    lastPlayedDate: today,
    totalDaysPlayed: totalDaysPlayed + 1,
    streakFreezeUsed: newStreakFreezeUsed,
    activityDates: newActivityDates,
    lastUpdated: new Date().toISOString()
  };
};

/**
 * Get streak status
 */
export const getStreakStatus = (streakData = {}) => {
  const currentStreak = streakData.currentStreak || 0;
  const lastPlayedDate = streakData.lastPlayedDate;
  const today = getTodayString();
  const yesterday = getYesterdayString();

  if (!lastPlayedDate) {
    return {
      status: 'new',
      message: 'Start your streak today!',
      color: '#3498db',
      emoji: '🌱'
    };
  }

  if (lastPlayedDate === today) {
    return {
      status: 'active',
      message: `${currentStreak} day streak! 🔥`,
      color: '#2ecc71',
      emoji: '✅'
    };
  }

  if (lastPlayedDate === yesterday) {
    return {
      status: 'pending',
      message: 'Play today to continue your streak!',
      color: '#f39c12',
      emoji: '⏰'
    };
  }

  return {
    status: 'broken',
    message: 'Streak broken. Start a new one!',
    color: '#e74c3c',
    emoji: '💔'
  };
};

/**
 * Get streak badges earned
 */
export const getStreakBadges = (streakData = {}) => {
  const currentStreak = streakData.currentStreak || 0;
  const longestStreak = streakData.longestStreak || 0;

  const badges = [];

  // Current streak badges
  if (currentStreak >= 7) {
    badges.push({
      id: 'week_warrior',
      name: 'Week Warrior',
      description: '7-day streak!',
      icon: '📅',
      color: '#3498db',
      earned: true
    });
  }

  if (currentStreak >= 30) {
    badges.push({
      id: 'month_master',
      name: 'Month Master',
      description: '30-day streak!',
      icon: '📆',
      color: '#f39c12',
      earned: true
    });
  }

  if (currentStreak >= 100) {
    badges.push({
      id: 'consistency_king',
      name: 'Consistency King',
      description: '100-day streak!',
      icon: '👑',
      color: '#e74c3c',
      earned: true
    });
  }

  // Longest streak badges
  if (longestStreak >= 7 && currentStreak < 7) {
    badges.push({
      id: 'week_warrior_past',
      name: 'Week Warrior (Past)',
      description: 'Achieved 7-day streak',
      icon: '📅',
      color: '#95a5a6',
      earned: false
    });
  }

  return badges;
};

/**
 * Get activity calendar data
 * @param {Array} activityDates - Array of date strings
 * @param {number} months - Number of months to show
 * @returns {Array} Calendar data
 */
export const getActivityCalendar = (activityDates = [], months = 3) => {
  const calendar = [];
  const today = new Date();

  for (let i = months - 1; i >= 0; i--) {
    const monthDate = new Date(today.getFullYear(), today.getMonth() - i, 1);
    const year = monthDate.getFullYear();
    const month = monthDate.getMonth();
    const monthName = monthDate.toLocaleString('default', { month: 'long' });
    
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const firstDayOfWeek = new Date(year, month, 1).getDay();
    
    const days = [];
    
    // Add empty days for alignment
    for (let j = 0; j < firstDayOfWeek; j++) {
      days.push({ date: null, active: false });
    }
    
    // Add actual days
    for (let day = 1; day <= daysInMonth; day++) {
      const dateString = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      const isActive = activityDates.includes(dateString);
      const isToday = dateString === getTodayString();
      const isFuture = new Date(dateString) > today;
      
      days.push({
        date: day,
        dateString,
        active: isActive,
        isToday,
        isFuture
      });
    }
    
    calendar.push({
      year,
      month,
      monthName,
      days
    });
  }

  return calendar;
};

/**
 * Get streak statistics
 */
export const getStreakStats = (streakData = {}) => {
  const currentStreak = streakData.currentStreak || 0;
  const longestStreak = streakData.longestStreak || 0;
  const totalDaysPlayed = streakData.totalDaysPlayed || 0;
  const activityDates = streakData.activityDates || [];
  
  // Calculate this month's activity
  const today = new Date();
  const thisMonth = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}`;
  const thisMonthDays = activityDates.filter(date => date.startsWith(thisMonth)).length;
  
  // Calculate this week's activity
  const weekAgo = new Date();
  weekAgo.setDate(weekAgo.getDate() - 7);
  const weekAgoString = weekAgo.toISOString().split('T')[0];
  const thisWeekDays = activityDates.filter(date => date >= weekAgoString).length;

  return {
    currentStreak,
    longestStreak,
    totalDaysPlayed,
    thisMonthDays,
    thisWeekDays,
    freezeAvailable: !streakData.streakFreezeUsed
  };
};

/**
 * Get motivational message based on streak
 */
export const getStreakMessage = (currentStreak) => {
  if (currentStreak === 0) {
    return {
      title: 'Start Your Journey! 🌱',
      message: 'Begin your learning streak today!',
      color: '#3498db'
    };
  } else if (currentStreak === 1) {
    return {
      title: 'Great Start! 🎯',
      message: 'Keep going tomorrow!',
      color: '#2ecc71'
    };
  } else if (currentStreak < 7) {
    return {
      title: `${currentStreak} Days Strong! 💪`,
      message: `${7 - currentStreak} more days to Week Warrior!`,
      color: '#3498db'
    };
  } else if (currentStreak < 30) {
    return {
      title: `Amazing ${currentStreak} Days! 🔥`,
      message: `${30 - currentStreak} more days to Month Master!`,
      color: '#f39c12'
    };
  } else if (currentStreak < 100) {
    return {
      title: `Incredible ${currentStreak} Days! ⭐`,
      message: `${100 - currentStreak} more days to Consistency King!`,
      color: '#e74c3c'
    };
  } else {
    return {
      title: `Legendary ${currentStreak} Days! 👑`,
      message: 'You are a true champion!',
      color: '#9b59b6'
    };
  }
};

/**
 * Save streak data to localStorage
 */
export const saveStreakData = (profileId, streakData) => {
  try {
    const key = `streak_${profileId}`;
    localStorage.setItem(key, JSON.stringify(streakData));
  } catch (error) {
    console.error('Error saving streak data:', error);
  }
};

/**
 * Load streak data from localStorage
 */
export const loadStreakData = (profileId) => {
  try {
    const key = `streak_${profileId}`;
    const data = localStorage.getItem(key);
    return data ? JSON.parse(data) : {
      currentStreak: 0,
      longestStreak: 0,
      lastPlayedDate: null,
      totalDaysPlayed: 0,
      streakFreezeUsed: false,
      activityDates: []
    };
  } catch (error) {
    console.error('Error loading streak data:', error);
    return {
      currentStreak: 0,
      longestStreak: 0,
      lastPlayedDate: null,
      totalDaysPlayed: 0,
      streakFreezeUsed: false,
      activityDates: []
    };
  }
};

/**
 * Check if streak needs update and update if necessary
 */
export const checkAndUpdateStreak = (profileId) => {
  const streakData = loadStreakData(profileId);
  const updatedData = updateStreak(streakData);
  
  if (JSON.stringify(streakData) !== JSON.stringify(updatedData)) {
    saveStreakData(profileId, updatedData);
    return { updated: true, data: updatedData };
  }
  
  return { updated: false, data: streakData };
};
